import { Nullable } from 'global';
import { BasePrice, ChargeAttachType } from 'modules/equipment/types';
import { QuoteCalcAttachType, QuoteCalcLineItemType, QuoteCalcRowInput } from '@trnsact/trnsact-shared-types';

import { QuoteChargeListingEntity, QuoteLineItemType } from '../types';
import { QuoteCalcRowPrice, QuoteCalcViewModel } from '../api/types';

class QuoteCalcMapper {
  public toPreviewRows(rows: QuoteChargeListingEntity[] = []): QuoteCalcRowInput[] {
    return rows.map(row => {
      if (row.type === QuoteLineItemType.Equipment && row.equipment) {
        return {
          type: QuoteCalcLineItemType.Equipment,
          equipment: {
            equipmentId: row.equipment.equipmentId,
            price: this.toCalcPrice(row.equipment.price),
            attachments: (row.equipment.attachments ?? []).map(attachment => ({
              price: this.toCalcPrice(attachment.price),
            })),
          },
        };
      }

      const charge = row.charge;

      return {
        type: QuoteCalcLineItemType.Invoice,
        charge: charge
          ? {
              id: charge.id ?? null,
              parentId: charge.parentId ?? null,
              parentType: charge.parentType ?? null,
              type: charge.type,
              label: charge.label ?? null,
              amount: charge.amount ?? null,
              amountType: charge.amountType ?? null,
              isTaxable: charge.isTaxable ?? null,
              isFromCommonRules: charge.isFromCommonRules ?? false,
              customerVisibility: charge.customerVisibility ?? null,
              price: this.toCalcPrice(charge.price),
              groupBucket: {
                id: charge.groupBucket.id,
                type:
                  charge.groupBucket.type === ChargeAttachType.Equipment
                    ? QuoteCalcAttachType.Equipment
                    : QuoteCalcAttachType.Invoice,
              },
            }
          : null,
      };
    });
  }

  public applyCalcToRows(
    rows: QuoteChargeListingEntity[] = [],
    calc: Nullable<QuoteCalcViewModel>
  ): QuoteChargeListingEntity[] {
    if (!calc?.rows?.length) return rows;

    return rows.map((row, index) => {
      const calcRow = calc.rows[index];

      if (!calcRow) return row;

      if (row.type === QuoteLineItemType.Equipment && row.equipment && calcRow.equipment) {
        const attachments = (row.equipment.attachments ?? []).map((attachment, attachmentIndex) => {
          const calcAttachment = calcRow.equipment?.attachments?.[attachmentIndex];

          return calcAttachment
            ? { ...attachment, price: this.mergeCalcPrice(attachment.price, calcAttachment.price) }
            : attachment;
        });

        return {
          ...row,
          equipment: {
            ...row.equipment,
            attachments,
            price: this.mergeCalcPrice(row.equipment.price, calcRow.equipment.price),
          },
        };
      }

      if (row.type === QuoteLineItemType.Invoice && row.charge && calcRow.charge) {
        return {
          ...row,
          charge: {
            ...row.charge,
            price: this.mergeCalcPrice(row.charge.price, calcRow.charge.price),
          },
        };
      }

      return row;
    });
  }

  private mergeCalcPrice(price: BasePrice, calcPrice: QuoteCalcRowPrice): BasePrice {
    return {
      ...price,
      total: calcPrice.total ?? price.total,
      netPrice: calcPrice.netPrice ?? price.netPrice,
      retailPrice: calcPrice.retailPrice ?? price.retailPrice,
    };
  }

  private toCalcPrice = (price: BasePrice) => ({
    total: price.total ?? 0,
    markup: price.markup ?? 0,
    netPrice: price.netPrice ?? 0,
    discount: price.discount ?? 0,
    salesTax: price.salesTax ?? 0,
    markupType: price.markupType,
    retailPrice: price.retailPrice ?? 0,
    discountType: price.discountType,
    salesTaxType: price.salesTaxType,
    wholesalePrice: price.wholesalePrice ?? 0,
  });
}

export const quoteCalcMapper = new QuoteCalcMapper();
